import React, { useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import _ from "lodash";
import { DispatchContext } from "../../state/Context";
import firebase from "../../services/firebase";
import { removeUserActivityInfoFromStorage } from "../../utils/userActivity";
import { clearMfaSession, setMfaSession } from "../../utils/mfaSession";
import AuthPageLayout from "./AuthPageLayout";
import {
  getLoginMfaStatus,
  initiateLoginMfa,
  resendLoginMfaCode,
  setupLoginMfaPhone,
  verifyLoginMfaCode,
  verifyLoginMfaPhoneSetup,
} from "../../services/loginMfa";
import { DEV_ACCOUNTS, GEO_RESTRICTION_BYPASS_WHITELIST } from "../../constants";
import useTimer from "./useTimer";

const formatPhoneInput = (value) => value.replace(/\D/g, "").slice(0, 10);

const SignInForm = ({ onLoginComplete }) => {
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [availableMethods, setAvailableMethods] = useState([]);
  const [emailVerifiedForMfa, setEmailVerifiedForMfa] = useState(false);
  const [mfaChannel, setMfaChannel] = useState("");
  const [step, setStep] = useState("credentials");
  const [errorText, setErrorText] = useState("");
  const [infoText, setInfoText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendBtnDisabled, setSendBtnDisabled] = useState(false);
  const { startTimer, countdown } = useTimer();
  const dispatch = useContext(DispatchContext);

  const setLoading = (isLoading, loadingText = "") => {
    dispatch({
      type: "SET_LOADING",
      payload: { isLoading, loadingText },
    });
  };

  const beginVerifyStep = (result) => {
    setSessionId(result.sessionId);
    setMfaChannel(result.channel || "sms");
    if (result.maskedPhone) {
      setMaskedPhone(result.maskedPhone);
    }
    if (result.maskedEmail) {
      setMaskedEmail(result.maskedEmail);
    }
    setStep("verify");
    setSendBtnDisabled(true);
    startTimer({ second: 20, onTimeout: () => setSendBtnDisabled(false) });
    if (result.debugCode) {
      setInfoText(`Development code: ${result.debugCode}`);
    }
  };

  const startMfaVerification = async (channel) => {
    try {
      const result = await initiateLoginMfa({ channel });
      beginVerifyStep(result);
    } catch (error) {
      const errorCode = error.response?.data?.code;
      if (errorCode === "PHONE_SETUP_REQUIRED" || errorCode === "MFA_SETUP_REQUIRED") {
        setStep("setup-phone");
        return;
      }
      throw error;
    }
  };

  const proceedAfterCredentialSignIn = async (status) => {
    setAvailableMethods(status.availableMethods || []);
    setMaskedPhone(status.maskedPhone || "");
    setMaskedEmail(status.maskedEmail || "");
    setEmailVerifiedForMfa(status.emailVerified === true);

    if (status.requiresPhoneSetup) {
      setStep("setup-phone");
      return;
    }

    const methods = status.availableMethods || [];
    if (methods.length > 1) {
      setStep("choose-method");
      return;
    }

    await startMfaVerification(methods[0] || "sms");
  };

  const handleSignIn = async (event) => {
    event.preventDefault();

    if (_.isEmpty(email) || _.isEmpty(password)) {
      setErrorText("Email and password are required.");
      return;
    }

    setErrorText("");
    setInfoText("");
    setLoading(true, "authenticating user ...");
    setIsSubmitting(true);

    try {
      removeUserActivityInfoFromStorage();
      clearMfaSession();

      const normalizedEmail = String(email || "")
        .trim()
        .toLowerCase();
      const isDevAccount = DEV_ACCOUNTS.includes(normalizedEmail);
      const isGeoRestrictionBypassUser = GEO_RESTRICTION_BYPASS_WHITELIST.includes(normalizedEmail);

      if (process.env.REACT_APP_ONLY_AU_USER === "true" && !isDevAccount && !isGeoRestrictionBypassUser) {
        const response = await fetch("https://ipapi.co/json/");
        const location = await response.json();
        if (location?.country_code && location?.country_code !== "AU") {
          setErrorText("Sorry, our services are currently available only within Australia.");
          return;
        }
      }

      await firebase.signInWithEmailAndPassword(email, password);

      try {
        const status = await getLoginMfaStatus();
        if (status.newLoginExperienceEnabled) {
          await proceedAfterCredentialSignIn(status);
          return;
        }
      } catch (error) {
        // Continue to local MFA check below.
      }

      const authUser = firebase.currentUser();
      const hasFirebaseMfa = (authUser?.multiFactor?.enrolledFactors?.length || 0) > 0;
      if (!hasFirebaseMfa) {
        try {
          const status = await getLoginMfaStatus();
          await proceedAfterCredentialSignIn(status);
          return;
        } catch (error) {
          setStep("setup-phone");
          return;
        }
      }

      clearMfaSession();
      history.push("/mfa-enroll");
    } catch (error) {
      if (error.code === "auth/multi-factor-auth-required") {
        window.resolver = error.resolver;
        history.push("/mfa-verify");
        return;
      }
      setErrorText(error.message || "Failed to sign in.");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleChooseMethod = async (channel) => {
    setErrorText("");
    setInfoText("");
    setIsSubmitting(true);

    try {
      await startMfaVerification(channel);
    } catch (error) {
      setErrorText(error.response?.data?.message || error.message || "Failed to send verification code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetupPhoneSubmit = async (event) => {
    event.preventDefault();
    setErrorText("");
    setInfoText("");
    setIsSubmitting(true);

    try {
      const formattedPhone = phoneNumber.startsWith("0") ? phoneNumber : `0${phoneNumber}`;
      const result = await setupLoginMfaPhone({ phoneNumber: formattedPhone });
      setSessionId(result.sessionId);
      setMfaChannel("sms");
      setMaskedPhone(result.maskedPhone || "");
      setStep("setup-phone-verify");
      setSendBtnDisabled(true);
      startTimer({ second: 20, onTimeout: () => setSendBtnDisabled(false) });
      if (result.debugCode) {
        setInfoText(`Development code: ${result.debugCode}`);
      }
    } catch (error) {
      setErrorText(error.response?.data?.message || error.message || "Failed to send verification code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetupPhoneVerifySubmit = async (event) => {
    event.preventDefault();
    setErrorText("");
    setInfoText("");
    setIsSubmitting(true);

    try {
      await verifyLoginMfaPhoneSetup({ sessionId, code: verificationCode });
      setVerificationCode("");
      setInfoText("Phone verified. Sending sign-in code...");
      const status = await getLoginMfaStatus();
      setAvailableMethods(status.availableMethods || []);
      setMaskedPhone(status.maskedPhone || "");
      setMaskedEmail(status.maskedEmail || "");
      setEmailVerifiedForMfa(status.emailVerified === true);

      const methods = status.availableMethods || [];
      if (methods.length > 1) {
        setStep("choose-method");
        return;
      }

      await startMfaVerification(methods[0] || "sms");
    } catch (error) {
      setErrorText(error.response?.data?.message || error.message || "Failed to verify phone number.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (sendBtnDisabled || isSubmitting) {
      return;
    }

    setErrorText("");
    setInfoText("");
    setIsSubmitting(true);

    try {
      const result =
        step === "setup-phone-verify"
          ? await setupLoginMfaPhone({
              phoneNumber: phoneNumber.startsWith("0") ? phoneNumber : `0${phoneNumber}`,
            })
          : await resendLoginMfaCode({ channel: mfaChannel });
      setSessionId(result.sessionId);
      if (result.maskedPhone) {
        setMaskedPhone(result.maskedPhone);
      }
      if (result.maskedEmail) {
        setMaskedEmail(result.maskedEmail);
      }
      setSendBtnDisabled(true);
      startTimer({ second: 20, onTimeout: () => setSendBtnDisabled(false) });
      setInfoText(
        result.debugCode ? `Development code: ${result.debugCode}` : "A new verification code has been sent."
      );
    } catch (error) {
      setErrorText(error.response?.data?.message || error.message || "Failed to resend code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySubmit = async (event) => {
    event.preventDefault();
    setErrorText("");
    setInfoText("");
    setIsSubmitting(true);

    try {
      const result = await verifyLoginMfaCode({ sessionId, code: verificationCode });
      const authUser = firebase.currentUser();
      setMfaSession({
        sessionId: result.sessionId,
        loginEmail: result.loginEmail || authUser?.email,
        firebaseAuthUid: result.firebaseAuthUid || authUser?.uid,
      });
      if (onLoginComplete) {
        await onLoginComplete();
      }
    } catch (error) {
      setErrorText(error.response?.data?.message || error.message || "Failed to verify code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getVerifyDestination = () => {
    if (mfaChannel === "email") {
      return maskedEmail || "your email";
    }
    return maskedPhone || "your phone";
  };

  const renderCredentialsForm = () => (
    <form className="public-secure-file-form" onSubmit={handleSignIn}>
      <div className="public-secure-file-notice">
        <i className="fas fa-shield-halved" aria-hidden="true" />
        <p>Sign in with your Telecare account email and password to continue.</p>
      </div>
      <label className="public-secure-file-label" htmlFor="sign-in-email">
        Email
      </label>
      <input
        id="sign-in-email"
        className={`public-secure-file-input ${errorText ? "has-error" : ""}`}
        type="email"
        autoComplete="username"
        value={email}
        onChange={(event) => {
          setErrorText("");
          setEmail(event.target.value);
        }}
        required
      />
      <label className="public-secure-file-label" htmlFor="sign-in-password">
        Password
      </label>
      <input
        id="sign-in-password"
        className={`public-secure-file-input ${errorText ? "has-error" : ""}`}
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(event) => {
          setErrorText("");
          setPassword(event.target.value);
        }}
        required
      />
      <Link to={{ pathname: "/admin/password/reset", state: { isResetPassword: true } }} className="is-size-7">
        Forgot your password?
      </Link>
      {errorText && (
        <p className="public-secure-file-inline-error" role="alert">
          <i className="fas fa-circle-exclamation" aria-hidden="true" />
          {errorText}
        </p>
      )}
      <button
        type="submit"
        className={`public-secure-file-button public-secure-file-button--primary ${isSubmitting ? "is-loading" : ""}`}
        disabled={isSubmitting}
      >
        Sign In
      </button>
    </form>
  );

  const renderChooseMethodForm = () => (
    <div className="public-secure-file-form">
      <div className="public-secure-file-notice">
        <i className="fas fa-shield-halved" aria-hidden="true" />
        <p>Choose how you would like to receive your verification code.</p>
      </div>
      {availableMethods.includes("sms") && (
        <button
          type="button"
          className={`public-secure-file-button public-secure-file-button--primary ${isSubmitting ? "is-loading" : ""}`}
          disabled={isSubmitting}
          onClick={() => handleChooseMethod("sms")}
        >
          <i className="fas fa-mobile-screen" aria-hidden="true" /> Text message to {maskedPhone || "your phone"}
        </button>
      )}
      {availableMethods.includes("email") && (
        <button
          type="button"
          className={`public-secure-file-button public-secure-file-button--primary ${isSubmitting ? "is-loading" : ""}`}
          disabled={isSubmitting}
          onClick={() => handleChooseMethod("email")}
        >
          <i className="fas fa-envelope" aria-hidden="true" /> Email to {maskedEmail || "your email"}
        </button>
      )}
      {errorText && (
        <p className="public-secure-file-inline-error" role="alert">
          <i className="fas fa-circle-exclamation" aria-hidden="true" />
          {errorText}
        </p>
      )}
    </div>
  );

  const renderSetupPhoneForm = () => (
    <form className="public-secure-file-form" onSubmit={handleSetupPhoneSubmit}>
      <div className="public-secure-file-notice">
        <i className="fas fa-mobile-screen" aria-hidden="true" />
        <p>
          Set up your mobile number for secure sign-in. We will send a verification code to confirm you own this number.
          {emailVerifiedForMfa
            ? " Once set up, you can also receive sign-in codes by email as a backup."
            : " If your email is verified later, you can use email codes as a backup too."}
        </p>
      </div>
      <label className="public-secure-file-label" htmlFor="sign-in-phone">
        Mobile number
      </label>
      <input
        id="sign-in-phone"
        className={`public-secure-file-input ${errorText ? "has-error" : ""}`}
        type="tel"
        inputMode="numeric"
        placeholder="04XXXXXXXX"
        value={phoneNumber}
        onChange={(event) => {
          setErrorText("");
          setPhoneNumber(formatPhoneInput(event.target.value));
        }}
        required
      />
      {errorText && (
        <p className="public-secure-file-inline-error" role="alert">
          <i className="fas fa-circle-exclamation" aria-hidden="true" />
          {errorText}
        </p>
      )}
      {infoText && <p className="is-size-7 has-text-info">{infoText}</p>}
      <button
        type="submit"
        className={`public-secure-file-button public-secure-file-button--primary ${isSubmitting ? "is-loading" : ""}`}
        disabled={isSubmitting || phoneNumber.length < 9}
      >
        Send Verification Code
      </button>
    </form>
  );

  const renderVerifyForm = (title, subtitle) => (
    <form
      className="public-secure-file-form"
      onSubmit={step === "setup-phone-verify" ? handleSetupPhoneVerifySubmit : handleVerifySubmit}
    >
      <div className="public-secure-file-notice">
        <i className={mfaChannel === "email" ? "fas fa-envelope" : "fas fa-message"} aria-hidden="true" />
        <p>{subtitle || `Enter the verification code sent to ${getVerifyDestination()}.`}</p>
      </div>
      {title && <p className="public-secure-file-document-label">{title}</p>}
      <label className="public-secure-file-label" htmlFor="sign-in-code">
        Verification code
      </label>
      <input
        id="sign-in-code"
        className={`public-secure-file-input ${errorText ? "has-error" : ""}`}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={verificationCode}
        onChange={(event) => {
          setErrorText("");
          setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6));
        }}
        required
      />
      {errorText && (
        <p className="public-secure-file-inline-error" role="alert">
          <i className="fas fa-circle-exclamation" aria-hidden="true" />
          {errorText}
        </p>
      )}
      {infoText && <p className="is-size-7 has-text-info">{infoText}</p>}
      <button
        type="submit"
        className={`public-secure-file-button public-secure-file-button--primary ${isSubmitting ? "is-loading" : ""}`}
        disabled={isSubmitting || verificationCode.length < 6}
      >
        Verify &amp; Continue
      </button>
      <button
        type="button"
        className={`public-secure-file-button ${sendBtnDisabled ? "is-loading" : ""}`}
        disabled={sendBtnDisabled || isSubmitting}
        onClick={handleResendCode}
      >
        {sendBtnDisabled ? `Resend in ${countdown}s` : "Resend Code"}
      </button>
      {step === "verify" && availableMethods.length > 1 && (
        <button
          type="button"
          className="public-secure-file-button"
          disabled={isSubmitting}
          onClick={() => {
            setVerificationCode("");
            setErrorText("");
            setInfoText("");
            setStep("choose-method");
          }}
        >
          Use a different method
        </button>
      )}
    </form>
  );

  const renderStepContent = () => {
    switch (step) {
      case "choose-method":
        return renderChooseMethodForm();
      case "setup-phone":
        return renderSetupPhoneForm();
      case "setup-phone-verify":
        return renderVerifyForm("Verify your phone number", `Enter the code sent to ${maskedPhone || "your phone"}.`);
      case "verify":
        return renderVerifyForm(null, null);
      default:
        return renderCredentialsForm();
    }
  };

  const stepSubtitle =
    step === "credentials"
      ? "Sign in to access your Telecare workflow portal"
      : step === "choose-method"
      ? "Choose how to verify your identity"
      : step === "setup-phone" || step === "setup-phone-verify"
      ? "Set up secure phone verification"
      : "Verify your identity to continue";

  return (
    <AuthPageLayout title="Sign In" subtitle={stepSubtitle}>
      {renderStepContent()}
    </AuthPageLayout>
  );
};

export default SignInForm;
